/**
 * Helper thuần cho modal nhập SP từ Salework + AMIS (TCR-2348).
 * Tách khỏi component để modal gọn và để test/đọc logic gọi API theo lô độc lập với UI.
 */
import * as XLSX from 'xlsx';
import type { ImportProductsBody, ImportRowResult, ProductImportTransportError } from '@/services/product.service';
import type { ImportRow, MergeResult } from './salework-amis-merge';

/** BE nhận tối đa 500 dòng/request và xử lý best-effort từng dòng → chia lô nhỏ để tránh timeout gateway. */
export const IMPORT_CHUNK_SIZE = 100;

export const MSG_FORBIDDEN = 'Bạn không có quyền tạo sản phẩm';

/** Đọc sheet thành mảng 2 chiều. `preferSheet` (vd 'KHO_HANG' của Salework) có thì dùng, không thì lấy sheet đầu. */
export async function readSheetRows(file: File, preferSheet?: string): Promise<unknown[][]> {
  const wb = XLSX.read(await file.arrayBuffer());
  const name = preferSheet && wb.SheetNames.includes(preferSheet) ? preferSheet : wb.SheetNames[0];
  return XLSX.utils.sheet_to_json<unknown[]>(wb.Sheets[name], { header: 1, defval: '' });
}

export interface AmisWorkbookSheet { name: string; rows: unknown[][] }

/** Đọc mọi sheet theo thứ tự trong workbook; Salework tiếp tục dùng readSheetRows(). */
export async function readWorkbookSheets(file: File): Promise<AmisWorkbookSheet[]> {
  const wb = XLSX.read(await file.arrayBuffer());
  return wb.SheetNames.map((name) => ({
    name,
    rows: XLSX.utils.sheet_to_json<unknown[]>(wb.Sheets[name], { header: 1, defval: '' }),
  }));
}

/** Cắt mảng thành các lô tối đa `size` phần tử, giữ nguyên thứ tự. */
export function chunk<T>(arr: T[], size = IMPORT_CHUNK_SIZE): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

/**
 * Message lỗi thân thiện từ lỗi axios/Error. Thứ tự ưu tiên:
 * 1) `data.error.message` — exception của repo trả `{ error: { code, message } }`;
 * 2) `data.message` (chuỗi hoặc phần tử đầu của mảng — class-validator);
 * 3) message axios; 4) chuỗi mặc định tiếng Việt.
 */
function errMessage(e: unknown): string {
  const x = e as {
    response?: { data?: { error?: { message?: string }; message?: string | string[] } };
    message?: string;
  };
  const data = x?.response?.data;
  const m = data?.message;
  return data?.error?.message || (Array.isArray(m) ? m[0] : m) || x?.message || 'Lỗi không xác định, vui lòng thử lại';
}

/**
 * Gửi lần lượt từng lô, gom kết quả theo đúng thứ tự dòng.
 * - Lô lỗi (mạng/5xx): đánh dấu mọi dòng của lô là `error` rồi chạy tiếp lô sau.
 * - 403: dừng hẳn (các lô còn lại cũng đánh dấu lỗi "không có quyền" để bảng kết quả đủ dòng).
 */
export async function importInChunks(
  rows: ImportRow[],
  base: Omit<ImportProductsBody, 'rows' | 'dry_run'>,
  send: (body: ImportProductsBody) => Promise<ImportRowResult[]>,
  onProgress?: (done: number, total: number, partial: ImportRowResult[]) => void,
  history?: { historyId: string; recordTransportErrors: (rows: ProductImportTransportError[]) => Promise<void> },
): Promise<ImportRowResult[]> {
  const results: ImportRowResult[] = [];
  const fail = (part: ImportRow[], message: string): ImportRowResult[] =>
    part.map((r) => ({ sku: r.sku, status: 'error', message }));
  const parts = chunk(rows);
  for (let i = 0; i < parts.length; i++) {
    const rowOffset = i * IMPORT_CHUNK_SIZE;
    try {
      const componentSkus = new Set(parts[i]
        .filter((row) => row.is_combo)
        .flatMap((row) => row.bom_components ?? [])
        .map((component) => component.sku));
      const previouslyFailedSkus = [...new Set(results
        .filter((result) => result.status === 'error' && componentSkus.has(result.sku))
        .map((result) => result.sku))];
      results.push(...(await send({ ...base, rows: parts[i], ...(previouslyFailedSkus.length ? { previously_failed_skus: previouslyFailedSkus } : {}), ...(history ? { history_id: history.historyId, history_row_offset: rowOffset } : {}) })));
    } catch (e) {
      if ((e as { response?: { status?: number } })?.response?.status === 403) {
        results.push(...fail(parts[i], MSG_FORBIDDEN));
        for (const rest of parts.slice(i + 1)) results.push(...fail(rest, MSG_FORBIDDEN));
        onProgress?.(rows.length, rows.length, [...results]);
        if (history) {
          const failedRows = rows.slice(rowOffset).map((row, index) => ({ row_index: rowOffset + index, sku: row.sku, message: MSG_FORBIDDEN }));
          for (const failedBatch of chunk(failedRows, 500)) await history.recordTransportErrors(failedBatch);
        }
        return results;
      }
      results.push(...fail(parts[i], errMessage(e)));
      if (history) {
        const failedRows = parts[i].map((row, index) => ({ row_index: rowOffset + index, sku: row.sku, message: errMessage(e) }));
        try {
          await history.recordTransportErrors(failedRows);
        } catch (recordError) {
          onProgress?.(results.length, rows.length, [...results]);
          throw recordError;
        }
      }
    }
    onProgress?.(results.length, rows.length, [...results]);
  }
  return results;
}

/** Tổng hợp kết quả chung và kết quả combo/vật tư theo đúng index BE trả về. */
export function summarize(results: ImportRowResult[], rows: ImportRow[]) {
  const paired = results.map((result, index) => ({ result, row: rows[index] }));
  return {
    created: results.filter((r) => r.status === 'created').length,
    updated: results.filter((r) => r.status === 'updated').length,
    existed: results.filter((r) => r.status === 'skipped_duplicate').length,
    error: results.filter((r) => r.status === 'error').length,
    comboCreated: paired.filter(({ result, row }) => row?.is_combo && result.status === 'created').length,
    comboUpdated: paired.filter(({ result, row }) => row?.is_combo && result.status === 'updated').length,
    comboBomError: paired.filter(({ result, row }) => row?.is_combo && result.status === 'error' && !!result.product_id).length,
    bomMaterialCreated: paired.filter(({ result, row }) => row?.is_bom_material && result.status === 'created').length,
    bomMaterialExisted: paired.filter(({ result, row }) => row?.is_bom_material && result.status === 'skipped_duplicate').length,
  };
}

/** Xuất báo cáo không khớp: sheet 'Không khớp' (mã chỉ có ở Salework) + 'AMIS trùng' (mã AMIS bị gộp). */
export function downloadUnmatchedReport(merge: MergeResult) {
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(
    wb,
    XLSX.utils.json_to_sheet(merge.unmatched.map((u) => ({ 'Mã': u.code, 'Tên': u.name, 'Giá': u.price, 'Barcode': u.barcode }))),
    'Không khớp',
  );
  XLSX.utils.book_append_sheet(
    wb,
    XLSX.utils.json_to_sheet(merge.duplicatesDropped.map((d) => ({ 'Mã': d.code, 'Dòng AMIS được giữ': d.kept }))),
    'AMIS trùng',
  );
  XLSX.writeFile(wb, `bao-cao-khong-khop-${new Date().toISOString().slice(0, 10)}.xlsx`);
}

/**
 * Xuất báo cáo dòng bị bỏ qua ở chế độ chỉ Salework (TCR-2420).
 * Khác báo cáo không khớp: không có khái niệm "AMIS trùng", chỉ có mã + lý do bỏ qua.
 */
export function downloadSkippedReport(skipped: { code: string; reason: string }[]) {
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(
    wb,
    XLSX.utils.json_to_sheet(skipped.map((s) => ({ 'Mã': s.code, 'Lý do': s.reason }))),
    'Dòng bị bỏ qua',
  );
  XLSX.writeFile(wb, `bao-cao-dong-bo-qua-${new Date().toISOString().slice(0, 10)}.xlsx`);
}
